// Updated: 2026-08-20T21:35:00Z
// Purpose: Manage persistent playback-buffering and future distributed-computing settings.

import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabaseClient";
import ServiceCaps from './ServiceCaps';
import GeminiQuotaSettings from './GeminiQuotaSettings';

type PlaybackBufferingSettings = {
  dynamic: boolean;
  scan_ahead_seconds: number;
};

type AdminSettingsResponse = {
  ok: boolean;
  settings?: PlaybackBufferingSettings;
  updated_at?: string | null;
  error?: string;
};

const DEFAULT_SETTINGS: PlaybackBufferingSettings = {
  dynamic: true,
  scan_ahead_seconds: 30,
};

async function describeFunctionError(error: unknown): Promise<string> {
  const context = (error as { context?: Response } | null)?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string };
      if (payload.error) return payload.error;
    } catch (parseError) {
      console.warn("[SettingsTab] Function error body was not JSON:", parseError);
    }
  }

  return error instanceof Error ? error.message : "Unknown settings error";
}

const SettingsTab: React.FC = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [minChunkSeconds, setMinChunkSeconds] = useState(180);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const requestId = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke<AdminSettingsResponse>(
        "admin-settings",
        {
          body: { action: "get" },
          headers: { "X-Request-ID": requestId },
        },
      );

      if (error) throw error;
      if (!data?.ok || !data.settings) {
        throw new Error(data?.error || "The settings service returned an incomplete response.");
      }

      setSettings(data.settings);
      setUpdatedAt(data.updated_at ?? null);
      console.debug("[SettingsTab] Playback settings loaded:", {
        requestId,
        settings: data.settings,
      });
    } catch (error) {
      const message = await describeFunctionError(error);
      console.error("[SettingsTab] Playback settings failed to load:", {
        message,
        error,
      });
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    setIsSaving(true);
    setLoadError(null);

    try {
      const requestId = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke<AdminSettingsResponse>(
        "admin-settings",
        {
          body: { action: "update", settings },
          headers: { "X-Request-ID": requestId },
        },
      );

      if (error) throw error;
      if (!data?.ok || !data.settings) {
        throw new Error(data?.error || "The settings service did not save the update.");
      }

      setSettings(data.settings);
      setUpdatedAt(data.updated_at ?? null);
      toast.success("Playback buffering settings saved.");
      console.debug("[SettingsTab] Playback settings saved:", {
        requestId,
        settings: data.settings,
      });
    } catch (error) {
      const message = await describeFunctionError(error);
      console.error("[SettingsTab] Playback settings failed to save:", {
        message,
        error,
      });
      setLoadError(message);
      toast.error(`Unable to save playback buffering settings: ${message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 text-slate-100">
      <GeminiQuotaSettings />
      <ServiceCaps />
      <section className="rounded-2xl border border-slate-700 bg-slate-900/40 p-5">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Playback buffering</h2>
          <p className="mt-1 text-sm text-slate-400">
            Prepare sentence audio before playback and keep generation ahead of the listener.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="dynamic-playback-buffering"
                checked={settings.dynamic}
                onCheckedChange={(checked) => setSettings((current) => ({
                  ...current,
                  dynamic: checked === true,
                }))}
                className="mt-0.5 border-slate-500"
              />
              <div>
                <Label
                  htmlFor="dynamic-playback-buffering"
                  className="cursor-pointer text-sm font-medium text-slate-100"
                >
                  Dynamic / automated buffering
                </Label>
                <p className="mt-1 text-xs text-slate-400">
                  Automatically increases scan-ahead when generation is slow or more blocks are missing.
                </p>
                <details className="mt-2 text-xs text-slate-400"><summary className="cursor-pointer text-emerald-300">Help: automated buffering</summary><p className="mt-2">Adjusts how far playback prepares ahead of the playhead. It can reduce audible gaps but does not raise provider quotas or worker concurrency. It never replaces the user’s exact voice directions.</p></details>
              </div>
            </div>

            <div className="max-w-sm space-y-2">
              <Label htmlFor="scan-ahead-seconds" className="text-sm text-slate-100">
                {settings.dynamic ? "Base scan-ahead buffer (seconds)" : "Fixed scan-ahead buffer (seconds)"}
              </Label>
              <Input
                id="scan-ahead-seconds"
                type="number"
                min={5}
                max={300}
                step={1}
                value={settings.scan_ahead_seconds}
                onChange={(event) => setSettings((current) => ({
                  ...current,
                  scan_ahead_seconds: Number.parseInt(event.target.value || "0", 10),
                }))}
                className="border-slate-600 bg-slate-950 text-slate-100"
              />
              <p className="text-xs text-slate-400">
                Allowed range: 5–300 seconds. The recommended starting value is 30 seconds.
              </p>
              <details className="text-xs text-slate-400"><summary className="cursor-pointer text-emerald-300">Help: scan-ahead seconds</summary><p className="mt-2">More look-ahead gives upcoming audio time to become ready but increases preparation and cache reads. It is not a faster-speech setting. Shorten it when fine-tuning a single block; lengthen it only if continuous playback develops gaps. Automated mode uses this as its base.</p></details>
            </div>

            {loadError && (
              <p className="rounded-lg border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
                {loadError}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                onClick={() => void saveSettings()}
                disabled={
                  isSaving ||
                  !Number.isInteger(settings.scan_ahead_seconds) ||
                  settings.scan_ahead_seconds < 5 ||
                  settings.scan_ahead_seconds > 300
                }
                className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save buffering settings
              </Button>
              {updatedAt && (
                <span className="text-xs text-slate-400">
                  Last saved {new Date(updatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-700 bg-slate-900/40 p-5">
        <div className="mb-2 text-lg font-medium">Distributed computing (future)</div>
        <div className="max-w-sm space-y-2">
          <Label htmlFor="minimum-chunk-seconds" className="text-xs text-slate-300">
            Minimum chunk length (seconds)
          </Label>
          <Input
            id="minimum-chunk-seconds"
            type="number"
            value={minChunkSeconds}
            onChange={(event) => setMinChunkSeconds(Number.parseInt(event.target.value || "0", 10))}
            className="border-slate-600 bg-slate-950 text-slate-100"
          />
          <p className="text-xs text-slate-400">
            Default ≈ 3 minutes (180 seconds). This future setting is not persisted yet.
          </p>
          <details className="text-xs text-slate-400"><summary className="cursor-pointer text-emerald-300">Help: future chunk length</summary><p className="mt-2">Placeholder only. It is not connected to current generation, cannot speed up conversions, and is not saved. Use recording-block controls above for the live planner.</p></details>
        </div>
      </section>
    </div>
  );
};

export default SettingsTab;
